import React, { useContext, useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import { Theme, useTheme } from "@mui/material/styles";

import Grid from "@mui/material/Grid";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import HomeIcon from "@mui/icons-material/Home";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";

import {
  Authorization,
  IFilterTemplate,
  Mode,
  TextAlign,
  abbreviate,
  authorized,
  formatLocalizedMoney,
  mergeProperties,
  packableDeepClone,
} from "packable-components";

import {
  PackableAppContext,
  SelectableChip,
  SelectableChipStyles,
} from "packable-components";
import { logDebug } from "packable-components";

import {
  ColumnDataType,
  IEntityTableActionColumn,
  IEntityTableColumn,
  IEntityDetailsProps,
} from "packable-components";

import {
  ItemType,
  IItem,
  ITag,
  LocationType,
  ShippingStatus,
  ItemDetails,
} from "packable-common";

import "./ItemTable.css";
import "./StorageItemTable.css";

import { IRowsPerPageBreakpoints } from "packable-common";
import { PackableStorageClientContext } from "../context/PackableStorageClientContext.types";
import { IconButton, Tooltip } from "@mui/material";
import {
  ShippingCart,
  ShippingCartItem,
  ShippingCartItemEntity,
  ShippingCartStatus,
  ShippingCartType,
} from "../shippingcart/ShippingCart.types";
import { StorageShippingCartIcon } from "../framework/StorageShippingCartIcon";
import { HomeShippingCartIcon } from "../framework/HomeShippingCartIcon";
import {
  IStorageItemPageProps,
  IStorageItemTableProps,
} from "./StorageItem.types";
import StorageItemTable from "./StorageItemTable";
import { IOrderProps } from "../orders/Order.types";
import OrderDetails from "../orders/OrderDetails";

const useStyles = (theme: Theme) => ({
  PackableEntityTableTableHead: {
    "& .MuiTableRow-root": {
      backgroundColor: theme.palette.tableHeader?.primary?.main,
      borderBottom: "solid",
      borderColor: theme.palette.divider,
      "& .MuiTableSortLabel-root": {
        color: theme.palette.tableHeader?.primary?.contrastText,
        fontWeight: theme.typography.body1.fontWeight,
        fontSize: theme.typography.body1.fontSize,
        "&:hover": {
          color: theme.palette.tableHeader?.primary?.contrastText,
        },
        "&:focus": {
          color: theme.palette.tableHeader?.primary?.contrastText,
          fontWeight: "bold",
        },
        "&:active": {
          color: theme.palette.tableHeader?.primary?.contrastText,
        },
        "& .MuiSvgIcon-root": {
          color: theme.palette.tableHeader?.primary?.contrastText,
        },
      },
      "& .MuiTableCell-root": {
        padding: "0.5rem !important",
      },
    },
  },
  PackableEntityTableTableBody: {
    "& .MuiTableRow-root": {
      borderBottom: "solid",
      borderColor: theme.palette.grey[400],
    },
    "& .MuiTableCell-root": {
      padding: "0.5rem !important",
    },
  },
  PackablePageAppBarTransferToStorageCartButton: {
    color: theme.palette.text.primary,
  },
  PackablePageAppBarTransferToStorageCartButtonAttention: {
    color: theme.palette.error.dark,
  },
  PackableItemTableItemNameCell: {
    display: "flex",
    flexDirection: "column",
  },
  PackableItemTableItemNameText: {
    muiVariant: "body1",
  },
  PackableItemTableItemDescriptionText: {
    fontSize: theme.typography.caption,
  },
  PackableItemTableItemNamePictureCountContainer: {
    pt: 1,
  },
  PackableItemTableItemNamePictureCountBadge: {
    muiColor: "primary",
  },
  PackableItemTableCategoryCell: {
    display: "flex",
    flexDirection: "column",
  },
  PackableItemTableCurrentInventoryIndicatorUnselected: {
    color: theme.palette.text.disabled,
  },
  ItemTableValueColumn: {
    textAlign: "right",
  },
  ItemTableValueHeader: {
    textAlign: "right",
  },
  ItemTableDescriptionColumn: {
    fontSize: theme.typography.caption,
  },
  ItemTableInstructionsColumn: {
    fontSize: theme.typography.caption,
  },
  PackableItemTableTagCell: {
    display: "flex",
    flexDirection: "column",
  },
  PackableItemTableTag: {
    padding: 0.25,
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
  ItemDetailsDialogContentAutocomplete: {
    "& .MuiSvgIcon-root": {
      color: theme.palette.text.primary,
    },
  },
  PackableFilterPopupMenuListSubheader: {
    fontSize: "20px",
    fontWeight: "700",
    color: theme.palette.text.primary,
  },
  PackableFilterPopupMenuListItem: {
    fontSize: "16px",
    color: theme.palette.text.primary,
  },
  PackableAlertDialogTitle: {
    ml: 1,
  },
  PackableAlertDialogTitleTypography: {
    muiVariant: "h6",
  },
  StorageItemPageSelectItemPickupDateDialogBody: {
    display: "flex",
    flexDirection: "column",
    width: "512px",
  },
  PackableAlertDialogActions: {
    mr: 1,
  },
  PackableAlertDialogOption1Button: {
    muiVariant: "outlined",
  },
  PackableAlertDialogOption2Button: {
    muiVariant: "contained",
  },
  StorageItemPageSelectPickupDateAlertRowContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "center",
  },
  StorageItemPageSelectPickupDateAlertContentShortTextFieldContainer: {
    padding: 1,
  },
  StorageItemPageSelectPickupDateAlertContentShortTextField: {
    width: "240px",
  },
  StorageItemPageSelectPickupDateAlertFullWidthTextFieldContainer: {
    width: "100%",
    padding: 1,
  },
  StorageItemPageSelectPickupDateAlertFullWidthTextField: {
    width: "100%",
  },
});

export const StorageItemPage: React.FC<IStorageItemPageProps> = (
  props: IStorageItemPageProps
): React.JSX.Element => {
  const {
    userAppState,
    clientEntity,
    displayMessage,
    windowWidth = 800,
    windowHeight = 800,
    styles,
  } = props;

  logDebug("StorageItemPage PROPS");
  logDebug(props);

  const params = useParams();

  logDebug("StorageItemPage params");
  logDebug(params);

  const theme = useTheme();

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  const { httpService, dataFilter, updateDataFilter } =
    useContext(PackableAppContext);

  const { shippingCart } = React.useContext(PackableStorageClientContext);

  const { onShippingCartUpdate } = useContext(PackableStorageClientContext);

  const [isOrderShowing, setIsOrderShowing] = useState<boolean>(false);

  const [orderItemId, setOrderItemId] = useState<string>("");

  const isAdminUser = (): boolean => {
    return authorized("system", Authorization.MANAGE);
  };

  const onShowOrder =
    (row: IItem) =>
    (event: any): void => {
      event.stopPropagation();
      setOrderItemId(row.id);
      setIsOrderShowing(true);
    };

  const onOrderClose = (): void => {
    setIsOrderShowing(false);
  };

  const updateDataFilterURLs = (inventoryId: string): void => {
    if (isAdminUser()) return;
    let currentDataFilter = dataFilter;
    let templateList: Array<IFilterTemplate> =
      currentDataFilter.filterTemplates;
    let updatedTemplateList = templateList.map((template: IFilterTemplate) => {
      if (
        template.lookupURL !== undefined &&
        template.lookupURL !== null &&
        template.lookupURL.length > 0
      ) {
        const position = template.lookupURL.lastIndexOf("/");
        let newURL: string = template.lookupURL.substring(0, position + 1);
        template.lookupURL = newURL + inventoryId;
      }
      return template;
    });
    if (updatedTemplateList !== undefined) {
      currentDataFilter.filterTemplates =
        updatedTemplateList as Array<IFilterTemplate>;
    }

    /*
    // check if quick filter already active
    const excludeQuickFilter = currentDataFilter.selectionFilters.find(
      (filter) => filter.id === EXCLUDE_DELETED_AND_EXPIRED_FILTER_UUID
    );
    if (excludeQuickFilter !== undefined) {
      let updatedFilterList =
        currentDataFilter.selectionFilters.filter(
          (filter) => filter.id !== EXCLUDE_DELETED_AND_EXPIRED_FILTER_UUID
        );
      currentDataFilter.selectionFilters = updatedFilterList;
      const quickFilterTemplate = currentDataFilter.quickFilters.find(
        (quickFilter) => quickFilter.id === EXCLUDE_DELETED_AND_EXPIRED_FILTER_UUID
      );
      if (quickFilterTemplate !== undefined) {
        quickFilterTemplate.isCheckedOut = false;
      }
    }
    currentDataFilter.active = false;
    */
    updateDataFilter(currentDataFilter);
  };

  useEffect(() => {
    updateDataFilterURLs(userAppState.currentInventoryId);
  }, []);

  const rowsPerPageBreakpoints: Array<IRowsPerPageBreakpoints> = [
    {
      minHeight: 0,
      maxHeight: 850,
      rowsPerPage: 5,
    },
    {
      minHeight: 811,
      maxHeight: 1070,
      rowsPerPage: 10,
    },
    {
      minHeight: 1071,
      maxHeight: 1330,
      rowsPerPage: 15,
    },
    {
      minHeight: 1331,
      maxHeight: 1590,
      rowsPerPage: 20,
    },
  ];

  const actionColumns: Array<IEntityTableActionColumn> = [];

  const isItemInShippingCart = (
    item: IItem,
    shippingCart: ShippingCart
  ): boolean => {
    if (shippingCart.cartStatus === ShippingCartStatus.ACTIVE) {
      return (
        shippingCart.cartItems?.findIndex(
          (cartItem) => cartItem.itemId === item.id
        ) !== -1
      );
    }
    return false;
  };

  const isInProcessing = (item: IItem): boolean => {
    let rc = true;
    if (
      item.itemLocation !== undefined &&
      item.itemLocation !== null &&
      item.itemLocation.shippingStatus !== null
    ) {
      return !(
        item.itemLocation.shippingStatus === ShippingStatus.PICKUP_COMPLETED
      );
    }
    return rc;
  };

  const isInputRequired = (item: IItem): boolean => {
    return (
      item.itemLocation !== undefined &&
      item.itemLocation !== null &&
      item.itemLocation.shippingStatus !== null &&
      item.itemLocation.shippingStatus === ShippingStatus.LABELS_DELIVERED
    );
  };

  const onAddToShippingCart =
    (item: IItem) =>
    (event: any): void => {
      event.stopPropagation();
      if (item.itemLocation?.locationType === LocationType.TRANSIT) {
        //
        return;
      }
      let endDate = new Date();
      endDate.setDate(endDate.getDate() + 31);
      let currentShippingCart = packableDeepClone(shippingCart);
      currentShippingCart.cartStatus = ShippingCartStatus.ACTIVE;
      currentShippingCart.cartType =
        item.itemLocation?.locationType === LocationType.HOME
          ? ShippingCartType.STORAGE
          : ShippingCartType.HOME;
      let shippingCartItem: ShippingCartItem = new ShippingCartItemEntity();
      shippingCartItem.itemId = item.id;
      shippingCartItem.itemLabel = item.label;
      shippingCartItem.itemLength = item.length | 0;
      shippingCartItem.itemWidth = item.width | 0;
      shippingCartItem.itemHeight = item.height | 0;
      shippingCartItem.itemWeight = item.weight | 0;
      shippingCartItem.storageEndDate = endDate;
      currentShippingCart.cartItems?.push(shippingCartItem);
      onShippingCartUpdate(currentShippingCart);
    };

  const onRemoveFromShippingCart =
    (item: IItem) =>
    (event: any): void => {
      event.stopPropagation();
      let currentShippingCart: ShippingCart = packableDeepClone(shippingCart);
      if (currentShippingCart.cartItems !== null) {
        const newCartItems: Array<ShippingCartItem> =
          currentShippingCart.cartItems.filter(
            (cartItem) => cartItem.itemId !== item.id
          );
        currentShippingCart.cartItems = newCartItems;
        if (newCartItems.length === 0) {
          currentShippingCart.cartItems = [];
          currentShippingCart.cartStatus = ShippingCartStatus.INACTIVE;
        }
        onShippingCartUpdate(currentShippingCart);
      }
    };

  const isDisabled = (item: IItem) => {
    return (
      (item.itemLocation?.locationType === LocationType.HOME &&
        shippingCart.cartType === ShippingCartType.HOME &&
        shippingCart.cartStatus === ShippingCartStatus.ACTIVE) ||
      (item.itemLocation?.locationType === LocationType.STORAGE &&
        shippingCart.cartType === ShippingCartType.STORAGE &&
        shippingCart.cartStatus === ShippingCartStatus.ACTIVE)
    );
  };

  const calculateRowsPerPage = (windowHeight: number) => {
    if (rowsPerPageBreakpoints !== undefined) {
      let breakpoint = rowsPerPageBreakpoints.find((b) => {
        return b.minHeight <= windowHeight && windowHeight <= b.maxHeight;
      });
      if (breakpoint !== undefined) {
        return breakpoint.rowsPerPage;
      } else {
        return 25;
      }
    }
    return 5;
  };

  const calculateRowsPerPageSelection = (rowsPerPage: number) => {
    if (rowsPerPage === 5) {
      return [5, 10, 25, 50, 100];
    }
    if (rowsPerPage === 10) {
      return [10, 25, 50, 100];
    }
    if (rowsPerPage === 15) {
      return [15, 50, 100];
    }
    if (rowsPerPage === 20) {
      return [20, 50, 100];
    }
    return [10, 25, 50, 100];
  };

  const deleteConfirmationMessage = (
    itemTitle: string,
    entity: any
  ): string => {
    return (
      "You are about to delete " +
      (entity !== undefined && entity.type !== undefined ? entity.type : "") +
      " " +
      (entity !== undefined && entity.label !== undefined ? entity.label : "") +
      ". There is no way to recover it when it is deleted. Click 'YES' to proceed"
    );
  };

  const renderItemLocation = (
    row: IItem,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <Grid
        className="PackableItemTableItemLocationCell"
        sx={effectiveStyles.PackableItemTableItemLocationCell}
      >
        {row.itemLocation?.locationType === LocationType.HOME &&
          !isItemInShippingCart(row, shippingCart) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Item is at home location">
                <IconButton
                  className="PackablePageAppBarTransferToStorageCartButton"
                  sx={
                    effectiveStyles.PackablePageAppBarTransferToStorageCartButton
                  }
                  disabled={isDisabled(row)}
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onAddToShippingCart(row)}
                >
                  <HomeIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        {row.itemLocation?.locationType === LocationType.HOME &&
          isItemInShippingCart(row, shippingCart) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Item is at home location">
                <IconButton
                  className="PackablePageAppBarRemoveFromStorageCartButton"
                  sx={
                    effectiveStyles.PackablePageAppBarRemoveFromStorageCartButton
                  }
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onRemoveFromShippingCart(row)}
                >
                  <StorageShippingCartIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        {row.itemLocation?.locationType === LocationType.STORAGE &&
          !isItemInShippingCart(row, shippingCart) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Item is at Storage">
                <IconButton
                  className="PackablePageAppBarTransferToStorageCartButton"
                  sx={
                    effectiveStyles.PackablePageAppBarTransferToStorageCartButton
                  }
                  disabled={isDisabled(row)}
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onAddToShippingCart(row)}
                >
                  <CorporateFareIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        {row.itemLocation?.locationType === LocationType.STORAGE &&
          isItemInShippingCart(row, shippingCart) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Item is in Shipping Cart">
                <IconButton
                  className="PackablePageAppBarRemoveFromStorageCartButton"
                  sx={
                    effectiveStyles.PackablePageAppBarRemoveFromStorageCartButton
                  }
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onRemoveFromShippingCart(row)}
                >
                  <HomeShippingCartIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        {row.itemLocation?.locationType === LocationType.TRANSIT &&
          !isInProcessing(row) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Item is in transit">
                <IconButton
                  className="PackablePageAppBarTransferToStorageCartButton"
                  sx={
                    effectiveStyles.PackablePageAppBarTransferToStorageCartButton
                  }
                  disabled={isDisabled(row)}
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onShowOrder(row)}
                >
                  <LocalShippingIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        {row.itemLocation?.locationType === LocationType.TRANSIT &&
          isInProcessing(row) &&
          !isInputRequired(row) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Processing Storage request">
                <IconButton
                  className="PackablePageAppBarTransferToStorageCartButton"
                  sx={
                    effectiveStyles.PackablePageAppBarTransferToStorageCartButton
                  }
                  disabled={isDisabled(row)}
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onShowOrder(row)}
                >
                  <AutorenewIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        {row.itemLocation?.locationType === LocationType.TRANSIT &&
          isInProcessing(row) &&
          isInputRequired(row) && (
            <Grid sx={{ pr: 2 }}>
              <Tooltip title="Processing Storage request. Your input is required!">
                <IconButton
                  className="PackablePageAppBarTransferToStorageCartButtonAttention"
                  sx={
                    effectiveStyles.PackablePageAppBarTransferToStorageCartButtonAttention
                  }
                  disabled={isDisabled(row)}
                  edge="end"
                  aria-label="outgoing transfer cart"
                  aria-haspopup="true"
                  onClick={onShowOrder(row)}
                >
                  <SyncProblemIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
      </Grid>
    );
  };

  const renderItemLabel = (
    row: any,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <Grid
        className="PackableItemTableItemNameCell"
        sx={effectiveStyles.PackableItemTableItemNameCell}
      >
        <Grid>
          <Typography
            className="PackableItemTableItemNameText"
            sx={effectiveStyles.PackableItemTableItemNameText}
          >
            {row.type}
          </Typography>
        </Grid>
        <Grid>
          <Typography
            className="PackableItemTableItemNameText"
            sx={effectiveStyles.PackableItemTableItemNameText}
          >
            {row.label}
          </Typography>
        </Grid>
        {row.mediaList && row.mediaList.length > 0 && (
          <Grid
            className="PackableItemTableItemNamePictureCountContainer"
            sx={effectiveStyles.PackableItemTableItemNamePictureCountContainer}
          >
            <Badge
              className="PackableItemTableItemNamePictureCountBadge"
              sx={effectiveStyles.PackableItemTableItemNamePictureCountBadge}
              badgeContent={row.mediaList.length}
              color={
                effectiveStyles.PackableItemTableItemNamePictureCountBadge
                  .muiColor
              }
            >
              <InsertPhotoIcon />
            </Badge>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderItemName = (
    row: any,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <Grid
        className="PackableItemTableItemNameCell"
        sx={effectiveStyles.PackableItemTableItemNameCell}
      >
        {row.type === ItemType.ITEM && (
          <Grid>
            <Typography
              className="PackableItemTableItemNameText"
              sx={effectiveStyles.PackableItemTableItemNameText}
            >
              {row.name}
            </Typography>
          </Grid>
        )}
        <Grid>
          <Typography
            className="PackableItemTableItemDescriptionText"
            sx={effectiveStyles.PackableItemTableItemDescriptionText}
            variant={
              effectiveStyles.PackableItemTableItemDescriptionText.muiVariant
            }
          >
            {row.description}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderCategory = (
    row: any,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <Grid sx={effectiveStyles.PackableItemTableCategoryCell}>
        <Grid>
          <Typography variant="body1">{row.category?.name || ""}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption">
            {row.subCategory?.name || ""}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderTags = (
    row: any,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    let itemRow = row as IItem;
    return (
      <Grid sx={effectiveStyles.PackableItemTableTagCell}>
        {itemRow.tags
          .sort((a, b) => (a.name < b.name ? -1 : a.name === b.name ? 0 : 1))
          .map((tag: ITag) => {
            return (
              <Grid
                className="PackableItemTableTag"
                sx={effectiveStyles.PackableItemTableTag}
                key={"tag-" + tag.name}
              >
                <SelectableChip
                  label={tag.name}
                  color={tag.color}
                  description={tag.description}
                  selected
                  selectableChipStyle={
                    SelectableChipStyles[
                      tag.color as keyof typeof SelectableChipStyles
                    ]
                  }
                  styles={effectiveStyles}
                />
              </Grid>
            );
          })}
      </Grid>
    );
  };

  const renderItemDetails = (
    row: any,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <Grid sx={effectiveStyles.PackableItemTableCategoryCell}>
        <Grid>
          <Typography variant="caption">Condition: {row.condition}</Typography>
        </Grid>
        <Grid>
          <Typography variant="caption">
            {`Dimensions: ${
              row.length && row.width && row.height
                ? `${row.length}x${row.width}x${row.height}`
                : ""
            } ${abbreviate(userAppState.units?.dimensionUnit || "")}`}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="caption">
            {`Weight: ${row.weight ? row.weight : ""} ${abbreviate(
              userAppState.units?.weightUnit || ""
            )}`}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderItemValue = (
    row: IItem,
    className?: string,
    parentStyles?: any
  ): React.JSX.Element => {
    return (
      <>
        {formatLocalizedMoney(
          row.value,
          0,
          userAppState.units?.currency,
          Intl.DateTimeFormat().resolvedOptions().locale
        )}
      </>
    );
  };

  const tableColumns: Array<IEntityTableColumn> = [
    {
      name: "itemLocationName",
      title: "Location",
      type: ColumnDataType.function,
      renderFunction: renderItemLocation,
      cellClassName: "ItemTableLocationColumn",
      headerClassName: "ItemTableLocationHeader",
    },
    {
      name: "label",
      title: "Label",
      type: ColumnDataType.function,
      renderFunction: renderItemLabel,
      cellClassName: "ItemTableLabelColumn",
      headerClassName: "ItemTableLabelHeader",
    },
    {
      name: "spaceName",
      title: "Space",
      type: ColumnDataType.string,
      cellClassName: "ItemTableSpaceColumn",
      headerClassName: "ItemTableSpaceHeader",
    },
    {
      name: "categoryName",
      title: "Category",
      type: ColumnDataType.function,
      renderFunction: renderCategory,
      cellClassName: "ItemTableCategoryColumn",
      headerClassName: "ItemTableCategoryHeader",
    },
    {
      name: "tagName",
      title: "Tags",
      type: ColumnDataType.function,
      renderFunction: renderTags,
      cellClassName: "ItemTableTagsColumn",
      headerClassName: "ItemTableTagsHeader",
    },
    {
      name: "name",
      title: "Contents",
      type: ColumnDataType.function,
      renderFunction: renderItemName,
      cellClassName: "ItemTableItemNameColumn",
      headerClassName: "ItemTableItemNameHeader",
    },
    {
      name: "instructions",
      title: "Instructions",
      type: ColumnDataType.string,
      cellClassName: "ItemTableInstructionsColumn",
      headerClassName: "ItemTableInstructionsHeader",
    },
    {
      name: "value",
      title: "Value",
      align: TextAlign.right,
      type: ColumnDataType.function,
      renderFunction: renderItemValue,
      cellClassName: "ItemTableValueColumn",
      headerClassName: "ItemTableValueHeader",
    },
    {
      name: "condition",
      title: "Details",
      type: ColumnDataType.function,
      renderFunction: renderItemDetails,
      cellClassName: "ItemTableDetailsColumn",
      headerClassName: "ItemTableDetailsHeader",
    },
  ];

  const rowsPerPage = calculateRowsPerPage(windowHeight);
  const rowsPerPageSelection = calculateRowsPerPageSelection(rowsPerPage);

  const displayDetails = (props: IEntityDetailsProps): React.JSX.Element => {
    return <ItemDetails {...props}></ItemDetails>;
  };

  const renderOrderDetails = (): React.JSX.Element => {
    const orderDetailsProps: IOrderProps = {
      entityId: orderItemId,
      detailsMode: Mode.update,
      isShowing: isOrderShowing,
      onEntityDetailsClose: onOrderClose,
      httpService: httpService,
      entityName: "order",
      entityTitle: "Orders",
      itemTitle: "Order",
      getItemURL: "/order/item",
      updateURL: "/order",
      updateOrderURL: "/order",
      deleteOrderURL: "/order",
      isAdmin: false,
      windowWidth: windowWidth,
      windowHeight: windowHeight,
      styles: effectiveStyles,
    };
    return <OrderDetails {...orderDetailsProps}></OrderDetails>;
  };

  const itemsTableProps: IStorageItemTableProps = {
    httpService: httpService,
    orderBy: "label",
    rowsPerPage: rowsPerPage,
    rowsPerPageSelection: rowsPerPageSelection,
    supportedItemTypes: [ItemType.BOX],
    itemType: ItemType.BOX,
    entityName: "item",
    entityTitle: "Items",
    itemTitle: "Item",
    tableColumns: tableColumns,
    actionColumns: actionColumns,
    getAllURL: `/item/inventory/${userAppState.currentInventoryId}`,
    getItemURL: "/item",
    addURL: "/item",
    updateURL: "/item",
    deleteURL: "/item",
    labelServiceURL: "/item_label/download",
    add: true,
    search: true,
    typeaheadSearch: true,
    searchString: params.searchString,
    closeOnAdd: false,
    closeOnUpdate: true,
    filter: true,
    dataFilter: dataFilter,
    onFilterUpdate: updateDataFilter,
    onAddClick: displayDetails,
    onRowClick: displayDetails,
    userAppState: userAppState,
    clientEntity: clientEntity,
    shippingCart: shippingCart,
    displayConfirmationMessage: true,
    deleteConfirmationMessage: deleteConfirmationMessage,
    displayMessage: displayMessage,
    styles: effectiveStyles,
  };

  return (
    <Grid
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        width: "100%",
        pb: 3,
      }}
    >
      {isOrderShowing && renderOrderDetails()}
      <StorageItemTable {...itemsTableProps} />
    </Grid>
  );
};

export default StorageItemPage;
